import { CloseOutlined, MoreOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import IconRound from 'assets/logos/icon-round.svg?react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Field, FieldDescription, FieldError, FieldLabel } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Spinner } from 'components/ui/spinner';
import { Textarea } from 'components/ui/textarea';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiMessageTemplatesManager from 'services/api/ApiMessageTemplatesManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { MessageTemplateDTO } from 'services/api/models/MessageTemplateDTO';
import { toast } from 'sonner';
import { conversationActions } from 'store/conversations';
import { selectCurrentUser } from 'store/users/selectors';

export type MessageInputProps = {
  disabled?: boolean;
  animatePlaceholder?: boolean;
  minRows?: number;
  onMessageSent?: () => void;
  containerStyle?: React.CSSProperties;
  /** Removes the left spacer and right margin so the input fits flush inside a card */
  compact?: boolean;
};

type TemplateFormValues = { name: string; message_text: string };
type TemplateFormErrors = Partial<Record<keyof TemplateFormValues, string>>;

const EMPTY_TEMPLATE_FORM: TemplateFormValues = { name: '', message_text: '' };

/** Ant Design's required-field rule, without a custom message: "<label> is required". */
function validateTemplateForm(values: TemplateFormValues, labels: TemplateFormValues): TemplateFormErrors {
  const errors: TemplateFormErrors = {};
  if (!values.name.trim()) errors.name = `${labels.name} is required`;
  if (!values.message_text.trim()) errors.message_text = `${labels.message_text} is required`;
  return errors;
}

type TemplateFormFieldsProps = {
  values: TemplateFormValues;
  errors: TemplateFormErrors;
  onChange: (values: TemplateFormValues) => void;
};

/**
 * The name + text fields of the template dialogs. The visible `*` keeps the
 * accessible names of the old required `Form.Item`s ("* Nom du modèle").
 */
function TemplateFormFields({ values, errors, onChange }: TemplateFormFieldsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <Field data-invalid={!!errors.name || undefined}>
        <FieldLabel htmlFor="name">
          <span className="text-destructive-text">*</span> {t('chats.templates.fields.name')}
        </FieldLabel>
        <Input
          id="name"
          maxLength={256}
          value={values.name}
          aria-invalid={!!errors.name || undefined}
          onChange={e => onChange({ ...values, name: e.target.value })}
        />
        <FieldError>{errors.name}</FieldError>
      </Field>
      <Field data-invalid={!!errors.message_text || undefined}>
        <FieldLabel htmlFor="message_text">
          <span className="text-destructive-text">*</span> {t('chats.templates.fields.messageText')}
        </FieldLabel>
        <Textarea
          id="message_text"
          rows={3}
          className="max-h-56"
          value={values.message_text}
          aria-invalid={!!errors.message_text || undefined}
          onChange={e => onChange({ ...values, message_text: e.target.value })}
        />
        <FieldDescription>{t('chats.templates.helperTags')}</FieldDescription>
        <FieldError>{errors.message_text}</FieldError>
      </Field>
    </div>
  );
}

const MessageInput: React.FC<MessageInputProps> = ({
  disabled = false,
  animatePlaceholder = false,
  minRows = 1,
  onMessageSent,
  containerStyle,
  compact = false,
}) => {
  // State
  const [inputValue, setInputValue] = useState<string>();

  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Selector
  const {
    conversationId: currentConversationId,
    inputText: currentInputText,
    conversationMetadata,
    replyingToMessage,
  } = useSelectConversations();
  const currentUser = useSelector(selectCurrentUser);

  const saveInputTextValueInStore = (text?: string) => {
    dispatch(
      conversationActions.updateConversationInputText({
        conversationId: currentConversationId,
        inputText: text,
      }),
    );
  };

  const saveInputText = (text?: string) => {
    if (currentConversationId) {
      // Existing conversation, save the text in Redux store
      saveInputTextValueInStore(text);
    } else {
      // New conversation (= no conversation id), save the text in local state
      setInputValue(text);
    }
  };

  function sendMessage(messageText: string) {
    if (!currentConversationId) {
      return ApiPatientConversationsManager.createPatientConversations({
        initialMessageText: messageText,
      });
    }

    const repliedToMessageId = replyingToMessage?.messageId;

    if (currentUser?.is_caregiver) {
      return ApiDoctorConversationsManager.postMessageToDoctorConversation({
        conversationId: currentConversationId,
        messageText,
        repliedToMessageId,
      });
    }

    return ApiPatientConversationsManager.postMessageToPatientConversations({
      conversationId: currentConversationId,
      messageText,
      repliedToMessageId,
    });
  }

  const { isPending: loading, mutate: run } = useMutation({
    mutationFn: (messageText: string) => sendMessage(messageText),
    onSuccess: () => {
      saveInputText(undefined);

      if (currentConversationId && replyingToMessage) {
        dispatch(
          conversationActions.setReplyingToMessage({
            conversationId: currentConversationId,
            replyingTo: undefined,
          }),
        );
      }
      // For new conversations (no currentConversationId), navigation is handled by
      // the conversation.created_by_patient WebSocket event in Shell.tsx so it fires even if
      // this component has already unmounted.
    },
    onError: () => {
      toast.error(t('chats.list.errors.sendMessage'));
    },
  });

  // Get the input text, either from the Redux store or local state
  const input = currentInputText || inputValue;
  // A text is present and no empty text
  const isValidInputValue = !!(input && input.length > 0);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidInputValue || !input) {
      return;
    }
    posthog.capture('chats.message_sent');
    run(input);
    if (onMessageSent) {
      onMessageSent();
    }
  };

  // animate placeholders within the textarea
  const placeholders = t<string, { returnObjects: true }, string[]>('chats.new.exampleMessages', {
    returnObjects: true,
  });
  const [placeholder, setPlaceholder] = useState('');
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const currentPlaceholder = placeholders[currentPlaceholderIndex];
    function nextAnimationTick() {
      if (placeholder.length < currentPlaceholder.length) {
        setPlaceholder(currentPlaceholder.slice(0, placeholder.length + 1));
      } else {
        setCurrentPlaceholderIndex((currentPlaceholderIndex + 1) % placeholders.length);
        setPlaceholder('');
      }
    }

    if (animatePlaceholder) {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(nextAnimationTick, placeholder.length === currentPlaceholder.length ? 1200 : 20);
    }
  }, [placeholder, currentPlaceholderIndex]);

  // Templates (caregivers only)
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [newTemplateModalOpen, setNewTemplateModalOpen] = useState(false);
  const [editTemplateModalOpen, setEditTemplateModalOpen] = useState(false);
  const [templateBeingEdited, setTemplateBeingEdited] = useState<MessageTemplateDTO | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<MessageTemplateDTO | null>(null);
  const [newTemplateForm, setNewTemplateForm] = useState<TemplateFormValues>(EMPTY_TEMPLATE_FORM);
  const [newTemplateErrors, setNewTemplateErrors] = useState<TemplateFormErrors>({});
  const [editTemplateForm, setEditTemplateForm] = useState<TemplateFormValues>(EMPTY_TEMPLATE_FORM);
  const [editTemplateErrors, setEditTemplateErrors] = useState<TemplateFormErrors>({});
  const templateFieldLabels: TemplateFormValues = {
    name: t('chats.templates.fields.name'),
    message_text: t('chats.templates.fields.messageText'),
  };
  const {
    run: loadTemplates,
    loading: loadingTemplates,
    data: templates,
  } = useRequest(() => ApiMessageTemplatesManager.getMessageTemplates(), {
    cacheKey: 'message-templates',
    manual: true,
  });

  const { run: createTemplate, loading: creatingTemplate } = useRequest(
    (params: { name: string; message_text: string }) => ApiMessageTemplatesManager.createMessageTemplate(params),
    {
      manual: true,
      onSuccess: () => {
        // Refresh list and insert created template into the input
        loadTemplates();
        setNewTemplateModalOpen(false);
        setNewTemplateForm(EMPTY_TEMPLATE_FORM);
        setNewTemplateErrors({});
      },
    },
  );

  const { run: updateTemplate, loading: updatingTemplate } = useRequest(
    (id: string, params: { name?: string; message_text?: string }) =>
      ApiMessageTemplatesManager.updateMessageTemplate(id, params),
    {
      manual: true,
      onSuccess: () => {
        loadTemplates();
        setEditTemplateModalOpen(false);
        setTemplateBeingEdited(null);
        setEditTemplateForm(EMPTY_TEMPLATE_FORM);
        setEditTemplateErrors({});
      },
    },
  );

  const { run: deleteTemplate, loading: deletingTemplate } = useRequest(
    (id: string) => ApiMessageTemplatesManager.deleteMessageTemplate(id),
    {
      manual: true,
      onSuccess: () => {
        loadTemplates();
      },
    },
  );

  function onTemplatePicked(t: MessageTemplateDTO) {
    // Set both form and store/local state to the template text
    let text = t.message_text;
    if (currentConversationId && conversationMetadata?.conversation?.patient) {
      const patient = conversationMetadata.conversation.patient;
      if (patient?.first_name) {
        text = text.replace(/@PR[EÉ]NOM/gi, patient.first_name);
      }
      if (patient?.last_name) {
        text = text.replace(/@NOM/gi, patient.last_name);
      }
      if (currentUser?.first_name) {
        text = text.replace(/@MON_PR[EÉ]NOM/gi, currentUser.first_name);
      }
      if (currentUser?.last_name) {
        text = text.replace(/@MON_NOM/gi, currentUser.last_name);
      }
    }
    saveInputText(text);
    setTemplatesOpen(false);
  }

  function openNewTemplateModal() {
    setTemplatesOpen(false);
    setNewTemplateModalOpen(true);
    // Prefill message with current input for convenience
    setNewTemplateForm({ name: '', message_text: input || '' });
    setNewTemplateErrors({});
  }

  function openEditTemplateModal(item: MessageTemplateDTO) {
    setTemplatesOpen(false);
    setTemplateBeingEdited(item);
    setEditTemplateForm({ name: item.name, message_text: item.message_text });
    setEditTemplateErrors({});
    setEditTemplateModalOpen(true);
  }

  function closeEditTemplateModal() {
    setEditTemplateModalOpen(false);
    setTemplateBeingEdited(null);
  }

  function submitEditTemplate() {
    const errors = validateTemplateForm(editTemplateForm, templateFieldLabels);
    setEditTemplateErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (templateBeingEdited) {
      updateTemplate(templateBeingEdited.id, {
        name: editTemplateForm.name,
        message_text: editTemplateForm.message_text,
      });
    }
  }

  function submitNewTemplate() {
    const errors = validateTemplateForm(newTemplateForm, templateFieldLabels);
    setNewTemplateErrors(errors);
    if (Object.keys(errors).length > 0) return;
    createTemplate({ name: newTemplateForm.name, message_text: newTemplateForm.message_text });
  }

  function confirmDeleteTemplate() {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
    }
    setTemplateToDelete(null);
  }

  useEffect(() => {
    if (currentUser?.is_caregiver) {
      loadTemplates();
    }
  }, []);

  const showTemplatesSpinner = loadingTemplates && (templates === undefined || templates.length === 0);

  return (
    <form className="z-[9] flex flex-wrap items-end p-3 pl-0" onSubmit={onSubmit} style={containerStyle}>
      {replyingToMessage && (
        <div className="w-full pb-3 pl-3">
          <div className="flex w-full items-center gap-2 relative overflow-hidden rounded-md bg-background px-3 py-2 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-success">
            <div className="flex-1 overflow-hidden">
              <div className="mb-0.5 text-sm font-medium text-foreground">{replyingToMessage.senderName}</div>
              <div className="overflow-hidden text-xs text-ellipsis whitespace-nowrap text-muted-foreground">
                {replyingToMessage.messageText}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                if (currentConversationId) {
                  dispatch(
                    conversationActions.setReplyingToMessage({
                      conversationId: currentConversationId,
                      replyingTo: undefined,
                    }),
                  );
                }
              }}
            >
              <CloseOutlined />
            </Button>
          </div>
        </div>
      )}
      {!currentUser?.is_caregiver && !compact && <div className="w-3"></div>}
      {currentUser?.is_caregiver && (
        <div className="flex items-end">
          <Popover
            open={templatesOpen}
            onOpenChange={open => {
              setTemplatesOpen(open);
              if (open) {
                posthog.capture('chats.caregiver-templates.opened');
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon-lg">
                <PlusOutlined />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" side="top" className="w-auto min-w-64 max-w-sm">
              <div className="max-h-80 overflow-y-auto pl-2 [scrollbar-width:none]">
                <div className="mb-4 flex items-center justify-between">
                  <h4>{t('chats.templates.title')}</h4>
                  <Button onClick={openNewTemplateModal}>
                    <PlusOutlined />
                    {t('common.new')}
                  </Button>
                </div>
                {showTemplatesSpinner ? (
                  <div className="flex justify-center py-4">
                    <Spinner className="size-6" />
                  </div>
                ) : (templates ?? []).length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">{t('common.empty')}</p>
                ) : (
                  <ul className="m-0 list-none divide-y divide-border p-0">
                    {(templates ?? []).map(item => {
                      const isOwner = item.created_by === currentUser?.id;
                      return (
                        <li
                          key={item.id}
                          className="flex cursor-pointer items-start gap-2 py-2"
                          onClick={() => {
                            onTemplatePicked(item);
                            posthog.capture('chats.caregiver-templates.template-picked', {
                              template_id: item.id,
                              template_name: item.name,
                            });
                          }}
                        >
                          <div className="min-w-0 flex-1">
                            <h5>
                              {isOwner ? (
                                item.name
                              ) : (
                                <span className="inline-flex items-center gap-1.5">
                                  <IconRound
                                    aria-hidden
                                    fill="currentColor"
                                    className="text-inverse"
                                    height={16}
                                    width={16}
                                  />
                                  <span>{item.name}</span>
                                </span>
                              )}
                            </h5>
                            <p className="m-0 text-sm whitespace-pre-wrap text-muted-foreground">
                              {item.message_text.length > 120
                                ? `${item.message_text.slice(0, 120)}…`
                                : item.message_text}
                            </p>
                          </div>
                          {isOwner && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="link" size="icon" onClick={e => e.stopPropagation()}>
                                  <MoreOutlined />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => openEditTemplateModal(item)}>
                                  {t('common.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setTemplatesOpen(false);
                                    setTemplateToDelete(item);
                                  }}
                                >
                                  {t('common.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
      <div className="me-0.5 min-w-0 flex-1">
        <Textarea
          name="message"
          required
          placeholder={animatePlaceholder ? placeholder : t('chats.fields.message.placeholder')}
          autoComplete="off"
          autoCorrect="on"
          rows={minRows}
          className="max-h-32 min-h-0 wrap-anywhere"
          value={input ?? ''}
          onChange={e => saveInputText(e.target.value)}
          data-testid="message-input"
        />
      </div>

      <div className="ml-2 flex items-end">
        <Button type="submit" size="icon-lg" disabled={loading || disabled || !isValidInputValue} loading={loading}>
          <SendOutlined />
        </Button>
      </div>

      <Dialog open={editTemplateModalOpen} onOpenChange={open => !open && closeEditTemplateModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chats.templates.editTemplate')}</DialogTitle>
          </DialogHeader>
          <TemplateFormFields values={editTemplateForm} errors={editTemplateErrors} onChange={setEditTemplateForm} />
          <DialogFooter>
            <Button variant="outline" onClick={closeEditTemplateModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitEditTemplate} loading={updatingTemplate}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={newTemplateModalOpen} onOpenChange={open => !open && setNewTemplateModalOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chats.templates.newTemplate')}</DialogTitle>
          </DialogHeader>
          <TemplateFormFields values={newTemplateForm} errors={newTemplateErrors} onChange={setNewTemplateForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTemplateModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitNewTemplate} loading={creatingTemplate}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={templateToDelete !== null} onOpenChange={open => !open && setTemplateToDelete(null)}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{t('chats.templates.confirmDelete')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateToDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTemplate} loading={deletingTemplate}>
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};
export default MessageInput;
